<template>
  <div>
    <NavigationBar
      showBackBtn
      titel="Dokumentvorlage"
      :actions="actions"
    ></NavigationBar>
    <section>
      <template>
        <v-container fluid>
          <v-row class="match-height" v-if="template">
            <!-- Template Info -->
            <v-col cols="12">
              <DocumentTemplateDetailCard
                :template="template"
                @download="downloadFile"
              />
            </v-col>

            <!-- Placeholder Reference -->
            <v-col cols="12" md="8">
              <v-card>
                <v-card-title class="body-2 v-card__subtitle">
                  <v-icon x-small class="mr-2">mdi-tag-multiple</v-icon
                  >Platzhalter
                </v-card-title>
                <v-card-text>
                  <!-- Detected placeholders from active version -->
                  <div v-if="activeVersionPlaceholders">
                    <div class="text-overline mb-1">Platzhalter-Zuordnung</div>
                    <PlaceholderMappingEditor
                      v-if="activeVersionMappings"
                      :parsedPlaceholders="activeVersionPlaceholders"
                      :mappings="activeVersionMappings"
                      :placeholderContext="template.placeholderContext"
                      @mappingsChange="updateActiveVersionMappings"
                    />
                    <div v-else>
                      <div
                        v-if="activeVersionPlaceholders.simple.length > 0"
                        class="mb-2"
                      >
                        <v-chip
                          v-for="p in activeVersionPlaceholders.simple"
                          :key="p.tag"
                          x-small
                          class="mr-1 mb-1"
                          :color="p.isKnown ? 'success' : 'warning'"
                          :outlined="p.isKnown"
                        >
                          <v-icon x-small left>{{
                            p.isKnown ? "mdi-check" : "mdi-alert-circle-outline"
                          }}</v-icon>
                          {{ p.tag }}
                        </v-chip>
                      </div>
                      <v-btn
                        x-small
                        text
                        color="primary"
                        @click="migrateActiveVersionMappings"
                        class="mt-1"
                      >
                        <v-icon x-small left>mdi-auto-fix</v-icon>
                        Zuordnung erstellen
                      </v-btn>
                    </div>
                  </div>
                  <div v-else class="text--secondary text-body-2">
                    Keine Platzhalter-Analyse verfügbar.
                  </div>
                  <v-divider class="my-3"></v-divider>
                  <!-- Available context placeholders reference -->
                  <v-expansion-panels flat>
                    <v-expansion-panel>
                      <v-expansion-panel-header class="px-0">
                        <span class="text-caption"
                          >Alle verfügbaren Platzhalter</span
                        >
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-list dense>
                          <v-list-item
                            v-for="placeholder in availablePlaceholders"
                            :key="placeholder.tag"
                            dense
                          >
                            <v-list-item-content>
                              <v-list-item-title>
                                <code>{{
                                  formatPlaceholderTag(placeholder.tag)
                                }}</code>
                              </v-list-item-title>
                              <v-list-item-subtitle>
                                {{ placeholder.label }}
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                        <v-alert
                          type="info"
                          text
                          dense
                          class="mt-2"
                          v-if="loopTag"
                        >
                          Schleife:
                          <code>{{ formatLoopTag(loopTag) }}</code>
                        </v-alert>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Version History -->
            <v-col cols="12">
              <v-card>
                <v-card-title class="body-2 v-card__subtitle">
                  <v-icon x-small class="mr-2">mdi-history</v-icon
                  >Versionsverlauf
                  <v-spacer></v-spacer>
                  <v-btn
                    depressed
                    small
                    color="primary"
                    @click="uploadDialog = true"
                  >
                    <v-icon left small>mdi-upload</v-icon>
                    Neue Version hochladen
                  </v-btn>
                </v-card-title>

                <v-card-text>
                  <v-card outlined>
                    <v-simple-table v-if="sortedVersions.length > 0" dense>
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th>Version</th>
                            <th>Dateiname</th>
                            <th>Hochgeladen am</th>
                            <th>Status</th>
                            <th class="text-right">Aktionen</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="version in sortedVersions"
                            :key="version.versionNumber"
                          >
                            <td>
                              <strong>v{{ version.versionNumber }}</strong>
                            </td>
                            <td>
                              <v-icon small class="mr-1">mdi-file-word</v-icon>
                              {{ version.fileName }}
                            </td>
                            <td>{{ formatDate(version.uploadedAt) }}</td>
                            <td>
                              <v-chip
                                x-small
                                :color="getStatusColor(version.status)"
                                dark
                                label
                              >
                                {{ getStatusLabel(version.status) }}
                              </v-chip>
                            </td>
                            <td class="text-right">
                              <v-btn
                                icon
                                x-small
                                @click="downloadFile(version.filePath)"
                                title="Herunterladen"
                              >
                                <v-icon small>mdi-download</v-icon>
                              </v-btn>
                              <v-btn
                                icon
                                x-small
                                color="success"
                                @click="activateVersion(version)"
                                :disabled="version.status === 'active'"
                                :loading="
                                  activatingVersion === version.versionNumber
                                "
                                title="Als aktive Version setzen"
                              >
                                <v-icon small>mdi-check-circle</v-icon>
                              </v-btn>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                    <div v-else class="text-center text--secondary py-4">
                      Noch keine Versionen vorhanden.
                    </div>
                  </v-card>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Export Runs History -->
            <v-col cols="12">
              <v-card>
                <v-card-title class="body-2 v-card__subtitle">
                  <v-icon x-small class="mr-2">mdi-file-export</v-icon
                  >Verwendungshistorie
                  <v-spacer></v-spacer>
                  <v-chip small>
                    {{ exportRuns.length }}
                    {{ exportRuns.length === 1 ? "Export" : "Exporte" }}
                  </v-chip>
                </v-card-title>
                <v-card-text>
                  <v-simple-table v-if="exportRuns.length > 0" dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>Datum</th>
                          <th>Benutzer</th>
                          <th>Version</th>
                          <th>Kontext</th>
                          <th class="text-right">Datensätze</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="run in exportRuns"
                          :key="run.meta ? run.meta.id : run.generatedAt"
                        >
                          <td>{{ formatTimestamp(run.generatedAt) }}</td>
                          <td>{{ getUserName(run.generatedBy) }}</td>
                          <td>
                            <v-chip x-small label outlined>
                              v{{ run.templateVersionNumber }}
                            </v-chip>
                          </td>
                          <td>
                            <v-chip x-small outlined>
                              <v-icon x-small left>{{
                                getUsageContextIcon(run.usageContext)
                              }}</v-icon>
                              {{ getUsageContextLabel(run.usageContext) }}
                            </v-chip>
                          </td>
                          <td class="text-right">
                            {{ run.recordIds ? run.recordIds.length : 0 }}
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                  <div v-else-if="loadingExportRuns" class="text-center py-4">
                    <v-progress-circular
                      indeterminate
                      size="24"
                      width="2"
                      color="primary"
                      class="mr-2"
                    ></v-progress-circular>
                    <span class="text--secondary text-body-2"
                      >Exportverlauf wird geladen...</span
                    >
                  </div>
                  <div v-else class="text-center text--secondary py-4">
                    <v-icon class="mb-2" color="grey lighten-1"
                      >mdi-file-export-outline</v-icon
                    >
                    <div class="text-body-2">
                      Noch keine Exporte durchgeführt.
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row v-else>
            <v-col cols="12">
              <v-skeleton-loader type="card"></v-skeleton-loader>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </section>

    <!-- Upload Version Dialog -->
    <v-dialog v-model="uploadDialog" max-width="700" persistent>
      <v-card>
        <v-card-title>
          <v-icon left>mdi-upload</v-icon>
          Neue Version hochladen
        </v-card-title>
        <v-card-text>
          <v-alert type="info" text dense class="mb-4">
            Eine neue Version wird als Entwurf hochgeladen. Sie können die
            Version testen und anschließend aktivieren.
          </v-alert>
          <FileUploadDropzone
            v-model="newFiles"
            :acceptedFileTypes="[
              '.docx',
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            ]"
          />
          <v-progress-linear
            v-if="uploadProgress > 0"
            :value="uploadProgress"
            color="primary"
            height="8"
            rounded
            class="mt-3"
          ></v-progress-linear>
          <div v-if="parsingPlaceholders" class="mt-3">
            <v-progress-linear
              indeterminate
              color="primary"
            ></v-progress-linear>
            <div class="text-caption text--secondary mt-1">
              Platzhalter werden analysiert...
            </div>
          </div>
          <v-card
            v-if="detectedPlaceholders && !parsingPlaceholders && newFile"
            outlined
            class="mt-3"
          >
            <v-card-title class="text-subtitle-2">
              <v-icon left small color="success">mdi-check-circle</v-icon>
              Platzhalter-Zuordnung
            </v-card-title>
            <v-card-text>
              <PlaceholderMappingEditor
                v-model="newVersionMappings"
                :parsedPlaceholders="detectedPlaceholders"
                :placeholderContext="template.placeholderContext"
              />
            </v-card-text>
          </v-card>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="closeUploadDialog">Abbrechen</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            depressed
            color="primary"
            @click="uploadNewVersion"
            :loading="uploading"
            :disabled="!newFile"
          >
            <v-icon left>mdi-upload</v-icon>
            Version hochladen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title>Vorlage löschen?</v-card-title>
        <v-card-text>
          Sind Sie sicher, dass Sie die Vorlage
          <strong>{{ template ? template.name : "" }}</strong> und alle
          zugehörigen Versionen löschen möchten? Diese Aktion kann nicht
          rückgängig gemacht werden.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Abbrechen</v-btn>
          <v-btn
            color="error"
            depressed
            @click="deleteTemplate"
            :loading="deleting"
          >
            Löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import NavigationBar from "@/components/_systemwide/NavigationBar.vue";
import FileUploadDropzone from "@/components/_systemwide/FileUploadDropzone.vue";
import PlaceholderMappingEditor from "@/components/_systemwide/PlaceholderMappingEditor.vue";
import DocumentTemplateDetailCard from "@/components/_systemwide/DocumentTemplateDetailCard.vue";
import {
  storage,
  TaskEvent,
  organizationCollection,
  FieldValue,
  auth,
  db,
} from "@/firebase.js";
import { MY_ORGANIZATION } from "@/store/modules.js";
import {
  GET_DOCUMENT_TEMPLATES,
  UPDATE_DOCUMENT_TEMPLATE,
  DELETE_DOCUMENT_TEMPLATE,
  GET_EXPORT_RUNS,
} from "@/store/action-types.js";
import {
  PROCESSES_DOCUMENT_TEMPLATES_update,
  PROCESSES_DOCUMENT_TEMPLATES_delete,
} from "@/data/permission-types.js";
import {
  PLACEHOLDER_CONTEXTS,
  USAGE_CONTEXTS,
  parsePlaceholders,
  automapPlaceholders,
  getEffectiveContexts,
} from "@/util/documentTemplateService.js";

export default {
  name: "document-template-details",
  components: {
    NavigationBar,
    FileUploadDropzone,
    PlaceholderMappingEditor,
    DocumentTemplateDetailCard,
  },
  data() {
    return {
      newFiles: [],
      uploadProgress: 0,
      uploading: false,
      deleteDialog: false,
      deleting: false,
      uploadDialog: false,
      activatingVersion: null,
      detectedPlaceholders: null,
      newVersionMappings: {},
      parsingPlaceholders: false,
      exportRuns: [],
      loadingExportRuns: false,
      userNameCache: {},
      actions: [
        {
          title: "Bearbeiten",
          permission: `${PROCESSES_DOCUMENT_TEMPLATES_update}`,
          icon: "mdi-pencil",
          actionStyle: "textBtn",
          function: this.editItem,
        },
        {
          title: "Löschen",
          permission: `${PROCESSES_DOCUMENT_TEMPLATES_delete}`,
          icon: "mdi-delete",
          actionStyle: "textBtn",
          function: this.openDeleteDialog,
        },
      ],
    };
  },
  computed: {
    template() {
      return this.$store.state.myOrganization.documentTemplates.find(
        (t) => t.meta.id === this.$route.params.itemId,
      );
    },
    sortedVersions() {
      if (!this.template || !this.template.versions) return [];
      return [...this.template.versions].sort(
        (a, b) => b.versionNumber - a.versionNumber,
      );
    },
    nextVersionNumber() {
      if (!this.template || !this.template.versions) return 1;
      return this.template.versions.length > 0
        ? Math.max(...this.template.versions.map((v) => v.versionNumber)) + 1
        : 1;
    },
    effectiveContexts() {
      if (!this.template) return [];
      return getEffectiveContexts(this.template.placeholderContext);
    },
    availablePlaceholders() {
      if (!this.template) return [];
      const placeholders = [];
      this.effectiveContexts.forEach((ctx) => {
        const config = PLACEHOLDER_CONTEXTS[ctx];
        if (config) placeholders.push(...config.placeholders);
      });
      return placeholders;
    },
    loopTag() {
      if (!this.template) return null;
      const ctx = PLACEHOLDER_CONTEXTS[this.template.placeholderContext];
      return ctx ? ctx.loopTag : null;
    },
    activeVersionPlaceholders() {
      if (!this.template || !this.template.activeVersion) return null;
      return this.template.activeVersion.parsedPlaceholders || null;
    },
    activeVersionMappings() {
      if (!this.template || !this.template.activeVersion) return null;
      const mappings = this.template.activeVersion.placeholderMappings;
      return mappings && Object.keys(mappings).length > 0 ? mappings : null;
    },
    newFile() {
      return this.newFiles.length > 0 ? this.newFiles[0] : null;
    },
  },
  created() {
    this.initialize();
  },
  watch: {
    newFile(newVal) {
      if (newVal) {
        this.parseNewFile();
      } else {
        this.detectedPlaceholders = null;
      }
    },
  },
  methods: {
    async parseNewFile() {
      if (!this.newFile || !this.template) return;
      this.parsingPlaceholders = true;
      try {
        const contexts = getEffectiveContexts(this.template.placeholderContext);
        this.detectedPlaceholders = await parsePlaceholders(
          this.newFile,
          contexts,
        );
        // Auto-map detected placeholders to known data fields
        this.newVersionMappings = automapPlaceholders(
          this.detectedPlaceholders,
          contexts,
        );
        console.log("Detected placeholders:", this.detectedPlaceholders);
      } catch (error) {
        console.error("Error parsing placeholders:", error);
        this.detectedPlaceholders = null;
        this.newVersionMappings = {};
      } finally {
        this.parsingPlaceholders = false;
      }
    },
    async updateActiveVersionMappings(newMappings) {
      if (!this.template || !this.template.activeVersion) return;
      try {
        const organizationId = this.$route.params.organizationId;
        const updatedActiveVersion = {
          ...this.template.activeVersion,
          placeholderMappings: newMappings,
        };
        const updatedVersions = (this.template.versions || []).map((v) =>
          v.versionNumber === updatedActiveVersion.versionNumber
            ? { ...v, placeholderMappings: newMappings }
            : v,
        );
        await this.$store.dispatch(
          `${MY_ORGANIZATION}${UPDATE_DOCUMENT_TEMPLATE}`,
          {
            organizationId,
            itemId: this.template.meta.id,
            activeVersion: updatedActiveVersion,
            versions: updatedVersions,
          },
        );
        await this.$store.dispatch(
          `${MY_ORGANIZATION}${GET_DOCUMENT_TEMPLATES}`,
          { organizationId },
        );
      } catch (error) {
        console.error("Error saving placeholder mappings:", error);
      }
    },
    async migrateActiveVersionMappings() {
      if (!this.template || !this.activeVersionPlaceholders) return;
      const contexts = getEffectiveContexts(this.template.placeholderContext);
      const mappings = automapPlaceholders(
        this.activeVersionPlaceholders,
        contexts,
      );
      await this.updateActiveVersionMappings(mappings);
    },
    async initialize() {
      if (this.$store.state.myOrganization.documentTemplates.length === 0) {
        await this.$store.dispatch(
          `${MY_ORGANIZATION}${GET_DOCUMENT_TEMPLATES}`,
          {
            organizationId: this.$route.params.organizationId,
          },
        );
      }
      this.fetchExportRuns();
    },
    async fetchExportRuns() {
      this.loadingExportRuns = true;
      try {
        const runs = await this.$store.dispatch(
          `${MY_ORGANIZATION}${GET_EXPORT_RUNS}`,
          {
            organizationId: this.$route.params.organizationId,
            templateId: this.$route.params.itemId,
            limit: 50,
          },
        );
        this.exportRuns = runs || [];
        // Resolve user display names for all unique UIDs
        const uids = [
          ...new Set(
            this.exportRuns
              .map((r) => r.generatedBy)
              .filter((uid) => uid && !this.userNameCache[uid]),
          ),
        ];
        await Promise.all(uids.map((uid) => this.resolveUserName(uid)));
      } catch (error) {
        console.error("Error fetching export runs:", error);
        this.exportRuns = [];
      } finally {
        this.loadingExportRuns = false;
      }
    },
    async resolveUserName(uid) {
      if (this.userNameCache[uid]) return;
      try {
        const doc = await db
          .collection("organizations")
          .doc(this.$route.params.organizationId)
          .collection("membershipsPublic")
          .doc(uid)
          .get();
        if (doc.exists) {
          this.$set(
            this.userNameCache,
            uid,
            doc.data().user.displayName || "Unbekannt",
          );
        } else {
          this.$set(this.userNameCache, uid, "Unbekannt");
        }
      } catch {
        this.$set(this.userNameCache, uid, "Unbekannt");
      }
    },
    getUserName(uid) {
      if (!uid) return "–";
      return this.userNameCache[uid] || uid.substring(0, 8) + "…";
    },
    getUsageContextLabel(ctx) {
      const config = USAGE_CONTEXTS[ctx];
      return config ? config.label : ctx || "–";
    },
    getUsageContextIcon(ctx) {
      const config = USAGE_CONTEXTS[ctx];
      return config ? config.icon : "mdi-help-circle-outline";
    },
    formatPlaceholderTag(tag) {
      return "{" + tag + "}";
    },
    formatLoopTag(tag) {
      return "{#" + tag + "}...{/" + tag + "}";
    },
    formatTimestamp(ts) {
      if (!ts) return "–";
      let date;
      if (ts.toDate) {
        date = ts.toDate();
      } else if (ts.seconds) {
        date = new Date(ts.seconds * 1000);
      } else {
        date = new Date(ts);
      }
      return date.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    getStatusColor(status) {
      switch (status) {
        case "active":
          return "success";
        case "draft":
          return "info";
        case "archived":
          return "grey";
        default:
          return "grey";
      }
    },
    getStatusLabel(status) {
      switch (status) {
        case "active":
          return "Aktiv";
        case "draft":
          return "Entwurf";
        case "archived":
          return "Archiviert";
        default:
          return status;
      }
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    async downloadFile(filePath) {
      try {
        const fileRef = storage.ref(filePath);
        const downloadUrl = await fileRef.getDownloadURL();
        window.open(downloadUrl, "_blank");
      } catch (error) {
        console.error("Error downloading file:", error);
      }
    },
    editItem() {
      this.$router.push({
        name: "processes-document-templates-edit",
        params: {
          organizationId: this.$route.params.organizationId,
          itemId: this.$route.params.itemId,
        },
      });
    },
    openDeleteDialog() {
      this.deleteDialog = true;
    },
    closeUploadDialog() {
      this.uploadDialog = false;
      this.newFiles = [];
      this.uploadProgress = 0;
      this.detectedPlaceholders = null;
      this.newVersionMappings = {};
    },
    async deleteTemplate() {
      this.deleting = true;
      try {
        await this.$store.dispatch(
          `${MY_ORGANIZATION}${DELETE_DOCUMENT_TEMPLATE}`,
          {
            organizationId: this.$route.params.organizationId,
            itemId: this.$route.params.itemId,
          },
        );
      } catch (error) {
        console.error("Error deleting template:", error);
      } finally {
        this.deleting = false;
        this.deleteDialog = false;
      }
    },
    async activateVersion(version) {
      this.activatingVersion = version.versionNumber;
      try {
        const updatedVersions = this.template.versions.map((v) => ({
          ...v,
          status:
            v.versionNumber === version.versionNumber ? "active" : "archived",
        }));

        const newActiveVersion = updatedVersions.find(
          (v) => v.versionNumber === version.versionNumber,
        );

        await this.$store.dispatch(
          `${MY_ORGANIZATION}${UPDATE_DOCUMENT_TEMPLATE}`,
          {
            organizationId: this.$route.params.organizationId,
            itemId: this.template.meta.id,
            activeVersion: newActiveVersion,
            versions: updatedVersions,
          },
        );

        // Refresh data
        await this.$store.dispatch(
          `${MY_ORGANIZATION}${GET_DOCUMENT_TEMPLATES}`,
          {
            organizationId: this.$route.params.organizationId,
          },
        );
      } catch (error) {
        console.error("Error activating version:", error);
      } finally {
        this.activatingVersion = null;
      }
    },
    async uploadNewVersion() {
      if (!this.newFile || !this.template) return;

      this.uploading = true;
      this.uploadProgress = 0;

      try {
        const organizationId = this.$route.params.organizationId;
        const versionNumber = this.nextVersionNumber;

        // 1. Parse placeholders if not already detected
        if (!this.detectedPlaceholders) {
          await this.parseNewFile();
        }

        // 2. Upload file to versioned storage path
        const storageRef = storage.ref();
        const filePath = `organizations/${organizationId}/documentTemplates/${this.template.meta.id}/v${versionNumber}/${this.newFile.name}`;
        const uploadTask = storageRef.child(filePath).put(this.newFile, {
          contentType:
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });

        await new Promise((resolve, reject) => {
          uploadTask.on(
            TaskEvent.STATE_CHANGED,
            (snapshot) => {
              this.uploadProgress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            },
            (error) => {
              reject(error);
            },
            async () => {
              // 3. Create new version entry as draft with filePath
              const versionEntry = {
                versionNumber,
                filePath,
                fileName: this.newFile.name,
                uploadedAt: new Date().toISOString(),
                uploadedBy: auth.currentUser.uid,
                status: "draft",
                parsedPlaceholders: this.detectedPlaceholders || {
                  simple: [],
                  loops: [],
                  images: [],
                },
                placeholderMappings: this.newVersionMappings || {},
              };

              // 4. Append to versions array
              const updatedVersions = [
                ...(this.template.versions || []),
                versionEntry,
              ];

              await organizationCollection
                .doc(organizationId)
                .collection("documentTemplates")
                .doc(this.template.meta.id)
                .update({
                  versions: updatedVersions,
                  "meta.lastUpdatedAt": FieldValue.serverTimestamp(),
                  "meta.lastUpdatedBy": auth.currentUser.uid,
                });
              resolve();
            },
          );
        });

        // 4. Refresh data
        await this.$store.dispatch(
          `${MY_ORGANIZATION}${GET_DOCUMENT_TEMPLATES}`,
          {
            organizationId,
          },
        );

        this.newFiles = [];
        this.uploadProgress = 0;
        this.detectedPlaceholders = null;
        this.newVersionMappings = {};
        this.uploadDialog = false;
      } catch (error) {
        console.error("Error uploading new version:", error);
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>
